import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { 
  CheckCircle2, ArrowRight, Copy, ExternalLink, Home, 
  RefreshCcw, Download, Clock, Share2, ArrowDownUp
} from 'lucide-react';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

const SwapConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [copied, setCopied] = useState(false);
  
  // Get swap details from navigation state
  const swapDetails = location.state?.swapDetails;

  // Theme colors
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textMuted = isDark ? 'text-white/60' : 'text-gray-600';
  const cardBg = isDark ? 'bg-[#111111]' : 'bg-white';
  const cardBorder = isDark ? 'border-white/10' : 'border-gray-200';

  useEffect(() => {
    // Trigger confetti on successful swap
    if (swapDetails?.success) {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
    }
  }, [swapDetails]);

  // If no swap details, show error or redirect
  if (!swapDetails) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className={`${cardBg} border ${cardBorder} rounded-2xl p-8 text-center max-w-md`}>
          <div className="w-16 h-16 rounded-full bg-yellow-500/10 flex items-center justify-center mx-auto mb-4">
            <Clock className="w-8 h-8 text-yellow-500" />
          </div>
          <h2 className={`text-xl font-bold ${textPrimary} mb-2`}>No Transaction Found</h2>
          <p className={`${textMuted} mb-6`}>
            We couldn't find the transaction details. Please try again.
          </p>
          <button
            onClick={() => navigate('/swap')}
            className="bg-purple-600 hover:bg-purple-700 text-white font-medium px-6 py-3 rounded-xl transition-all"
          >
            Go to Exchange
          </button>
        </div>
      </div>
    );
  }

  const {
    success,
    transactionId,
    swapMode,
    fromCurrency,
    toCurrency,
    fromAmount,
    toAmount,
    rate,
    feePercent,
    feeAmount,
    timestamp,
    estimatedValue
  } = swapDetails;

  const copyTransactionId = () => {
    navigator.clipboard.writeText(transactionId);
    setCopied(true);
    toast.success('Transaction ID copied');
    setTimeout(() => setCopied(false), 2000);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleShare = async () => {
    const shareText = `I just swapped ${fromAmount} ${fromCurrency} to ${toAmount} ${toCurrency} on Onerik! 🚀`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Onerik Swap',
          text: shareText,
          url: window.location.origin
        });
      } catch (err) {
        // User cancelled or error
      }
    } else {
      navigator.clipboard.writeText(shareText);
      toast.success('Share text copied to clipboard');
    }
  };

  const downloadReceipt = () => {
    const receipt = `
ONERIK SWAP RECEIPT
==================

Transaction ID: ${transactionId}
Date: ${formatDate(timestamp)}
Type: ${swapMode === 'fiat-crypto' ? 'Fiat to Crypto' : 'Crypto to Crypto'}

FROM
----
Amount: ${fromAmount} ${fromCurrency}

TO
--
Amount: ${toAmount} ${toCurrency}

DETAILS
-------
Exchange Rate: 1 ${fromCurrency} = ${rate < 1 ? rate.toFixed(8) : rate.toFixed(4)} ${toCurrency}
Fee (${feePercent}%): ${feeAmount < 0.0001 ? feeAmount.toFixed(8) : feeAmount.toFixed(6)} ${toCurrency}
${estimatedValue ? `Estimated Value: $${estimatedValue.toLocaleString()}` : ''}

Status: COMPLETED

==================
Thank you for using Onerik!
https://onerik.com
    `.trim();

    const blob = new Blob([receipt], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `onerik_swap_${transactionId.slice(0, 8)}.txt`;
    a.click();
    toast.success('Receipt downloaded');
  };

  return (
    <div data-testid="swap-confirmation-page" className="min-h-screen py-8 px-3 md:px-4">
      <div className="max-w-lg mx-auto">
        {/* Success Animation */}
        <div className="text-center mb-6">
          <div className={`relative inline-flex`}>
            <div className="w-20 h-20 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center animate-bounce-slow">
              <CheckCircle2 className="w-10 h-10 text-white" />
            </div>
            <div className="absolute -inset-1 rounded-full bg-green-500/20 animate-ping"></div>
          </div>
        </div>

        {/* Main Card */}
        <div className={`${cardBg} border ${cardBorder} rounded-2xl overflow-hidden shadow-xl`}>
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-6 text-center text-white">
            <h1 className="text-2xl font-bold mb-1">Swap Successful!</h1>
            <p className="text-white/80 text-sm">Your transaction has been completed</p>
          </div>

          {/* Swap Visual */}
          <div className="p-6">
            <div className="flex items-center justify-center gap-4 mb-6">
              {/* From */}
              <div className={`${isDark ? 'bg-white/5' : 'bg-gray-50'} rounded-xl p-4 text-center flex-1`}>
                <div className={`text-xs ${textMuted} mb-1`}>From</div>
                <div className={`text-2xl font-bold ${textPrimary}`}>
                  {parseFloat(fromAmount).toLocaleString(undefined, { maximumFractionDigits: 6 })}
                </div>
                <div className={`text-sm font-medium ${textMuted}`}>{fromCurrency}</div>
              </div>

              {/* Arrow */}
              <div className={`${isDark ? 'bg-white/10' : 'bg-gray-100'} rounded-full p-2`}>
                <ArrowRight className={`w-5 h-5 ${textPrimary}`} />
              </div>

              {/* To */}
              <div className={`${isDark ? 'bg-green-500/10' : 'bg-green-50'} rounded-xl p-4 text-center flex-1 border border-green-500/20`}>
                <div className="text-xs text-green-500 mb-1">To</div>
                <div className="text-2xl font-bold text-green-500">
                  {parseFloat(toAmount).toLocaleString(undefined, { maximumFractionDigits: 8 })}
                </div>
                <div className="text-sm font-medium text-green-600">{toCurrency}</div>
              </div>
            </div>

            {/* Transaction Details */}
            <div className="space-y-3 mb-6">
              <div className={`flex justify-between py-2 border-b ${cardBorder}`}>
                <span className={textMuted}>Transaction ID</span>
                <button 
                  onClick={copyTransactionId}
                  className={`flex items-center gap-1 font-mono text-sm ${textPrimary} hover:text-purple-500 transition-colors`}
                >
                  {transactionId.slice(0, 8)}...{transactionId.slice(-4)}
                  {copied ? (
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>

              <div className={`flex justify-between py-2 border-b ${cardBorder}`}>
                <span className={textMuted}>Type</span>
                <span className={`${textPrimary} font-medium`}>
                  {swapMode === 'fiat-crypto' ? 'Fiat → Crypto' : 'Crypto → Crypto'}
                </span>
              </div>

              <div className={`flex justify-between py-2 border-b ${cardBorder}`}>
                <span className={textMuted}>Exchange Rate</span>
                <span className={`${textPrimary} font-mono text-sm`}>
                  1 {fromCurrency} = {rate < 1 ? rate.toFixed(8) : rate.toFixed(4)} {toCurrency}
                </span>
              </div>

              <div className={`flex justify-between py-2 border-b ${cardBorder}`}>
                <span className={textMuted}>Fee ({feePercent}%)</span>
                <span className={`${textPrimary} font-mono text-sm`}>
                  {feeAmount < 0.0001 ? feeAmount.toFixed(8) : feeAmount.toFixed(6)} {toCurrency}
                </span>
              </div>

              <div className={`flex justify-between py-2 border-b ${cardBorder}`}>
                <span className={textMuted}>Date & Time</span>
                <span className={textPrimary}>{formatDate(timestamp)}</span>
              </div>

              {estimatedValue && (
                <div className={`flex justify-between py-2`}>
                  <span className={textMuted}>Estimated Value</span>
                  <span className={`text-green-500 font-medium`}>
                    ≈ ${estimatedValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </span>
                </div>
              )}
            </div>

            {/* Info Box */}
            <div className={`${isDark ? 'bg-blue-500/10 border-blue-500/20' : 'bg-blue-50 border-blue-200'} border rounded-xl p-4 mb-6`}>
              <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                {swapMode === 'fiat-crypto' ? (
                  <>Your {toCurrency} has been added to your wallet. You can view it in your Portfolio.</>
                ) : (
                  <>Your swap is complete. The {toCurrency} is now available in your wallet.</>
                )}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <button
                onClick={downloadReceipt}
                className={`flex items-center justify-center gap-2 py-3 rounded-xl ${
                  isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-100 hover:bg-gray-200'
                } ${textPrimary} font-medium transition-all`}
              >
                <Download className="w-4 h-4" />
                Receipt
              </button>
              <button
                onClick={handleShare}
                className={`flex items-center justify-center gap-2 py-3 rounded-xl ${
                  isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-100 hover:bg-gray-200'
                } ${textPrimary} font-medium transition-all`}
              >
                <Share2 className="w-4 h-4" />
                Share
              </button>
            </div>

            {/* Main Actions */}
            <div className="space-y-3">
              <button
                onClick={() => navigate('/swap')}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-medium py-4 rounded-xl transition-all flex items-center justify-center gap-2"
              >
                <ArrowDownUp className="w-5 h-5" />
                Make Another Swap
              </button>
              
              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/transactions')}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border ${cardBorder} ${textPrimary} font-medium hover:border-purple-500/50 transition-all`}
                >
                  <ExternalLink className="w-4 h-4" />
                  View Transactions
                </button>
                <button
                  onClick={() => navigate('/portfolio')}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border ${cardBorder} ${textPrimary} font-medium hover:border-purple-500/50 transition-all`}
                >
                  <Home className="w-4 h-4" />
                  Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className={`text-xs ${textMuted}`}>
            Need help? Contact our support team 24/7
          </p>
        </div>
      </div>

      {/* CSS for animation */}
      <style>{`
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default SwapConfirmation;
